<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import tabviewJustified from '@/presets/lara/tabview/tabviewJustified'

import IconTabHeader from '@/components/tabview/IconTabHeader.vue'
import GreigTabPanelContent from '@/components/features/batches/tab-content/GreigTabPanelContent.vue'
import OffsiteTabPanelContent
  from '@/components/features/batches/tab-content/OffsiteTabPanelContent.vue'
import FinishedTabPanelContent from '@/components/features/batches/tab-content/FinishedTabPanelContent.vue'
import AvailableTabPanelContent from '@/components/features/batches/tab-content/AvailableTabPanelContent.vue'
import DispatchedTabPanelContent from '@/components/features/batches/tab-content/DispatchedTabPanelContent.vue'
import {getBatchList, getReturnedBatchList, getAvailableBatches, getDispatchedBatches} from '@/api/batches'
import {type Batch, type BatchRoll, BatchStateValue, FireTestingStatus, QualityControlStatus} from '@/types/batch-types'

const activeTabIndex = ref(Number(localStorage.getItem('activeTabIndex')) || 0);
const batchList = ref<any>([])
const loading = ref<boolean>(true)
const greigBatches = ref<any>([])
const offsiteBatches =  ref<any>([])
const finishedBatches = ref<any>([])
const availableBatches = ref<any>([])
const dispatchedBatches = ref<any>([])

const batchHasRollsInState = (batch: Batch, rollState: number, allBatches: Batch[]) => {
  const hasCurrentRolls = batch.rolls && batch.rolls.some((roll: BatchRoll) => roll.rollState === rollState)

  const hasMovedRolls = allBatches.some((otherBatch: Batch) =>
      otherBatch.id !== batch.id &&
      otherBatch.rolls &&
      otherBatch.rolls.some((roll: BatchRoll) =>
          roll.previousBatchId === batch.id && roll.rollState === rollState
      )
  )

  return hasCurrentRolls || hasMovedRolls
}

async function fetchBatches() {
  const data = await getBatchList();
  batchList.value = removeDuplicates(data);

  greigBatches.value = batchList.value.filter((batch: Batch) =>
      batchHasRollsInState(batch, 0, batchList.value)
  );

  offsiteBatches.value = batchList.value.filter((batch: Batch) =>
      batchHasRollsInState(batch, 1, batchList.value)
  );

  const returnedBatches = await getReturnedBatchList();
  const batchStateMap = new Map();
  batchList.value.forEach((batch: { id: string, state: number }) => {
    batchStateMap.set(batch.id, batch.state);
  });

  finishedBatches.value = returnedBatches.map((batch: {
    previousBatchId?: string,
    qualityChecks?: { qualityCheckStatus: number }[],
    fireTests?: { fireTestingStatus: number }[]
  }) => {

         let qualityControlStatus;
     if (batch.qualityChecks?.every(qc => qc.qualityCheckStatus === QualityControlStatus.PENDING)) {
       qualityControlStatus = QualityControlStatus.PENDING;
     } else if (batch.qualityChecks?.some(qc => qc.qualityCheckStatus === QualityControlStatus.PASSED)) {
       qualityControlStatus = QualityControlStatus.PASSED;
     }else if (batch.qualityChecks?.some(qc => qc.qualityCheckStatus === QualityControlStatus.QUARANTINE)) {
       qualityControlStatus = QualityControlStatus.QUARANTINE;
     }else {
       qualityControlStatus = QualityControlStatus.PENDING;
     }

     let fireTestStatus;
     if (batch.fireTests?.every(ft => ft.fireTestingStatus === FireTestingStatus.PENDING)) {
       fireTestStatus = FireTestingStatus.PENDING;
     } else if (batch.fireTests?.some(ft => ft.fireTestingStatus === FireTestingStatus.PASSED)) {
       fireTestStatus = FireTestingStatus.PASSED;
     } else if (batch.fireTests?.some(ft => ft.fireTestingStatus === FireTestingStatus.FAILED)) {
       fireTestStatus = FireTestingStatus.FAILED;
     }else {
       fireTestStatus = FireTestingStatus.PENDING;
     }

    return {
      ...batch,
      qualityControlStatus: qualityControlStatus,
      fireTestStatus: fireTestStatus,
      previousBatchState: batch.previousBatchId ? batchStateMap.get(batch.previousBatchId) : undefined
    };
  }).filter((batch) => {
     if (batch.previousBatchState === BatchStateValue.DISPATCHED) {
       return false;
     }

    const hasPassedQualityCheck = batch.qualityChecks?.some(qc => qc.qualityCheckStatus === QualityControlStatus.PASSED);
    const hasPassedFireTest = batch.fireTests?.some(ft => ft.fireTestingStatus === FireTestingStatus.PASSED);

    if (hasPassedQualityCheck && hasPassedFireTest) {
      return false;
    }

    return true;
  });

  availableBatches.value = await getAvailableBatches();
  dispatchedBatches.value = await getDispatchedBatches();
}

function removeDuplicates(data: any[]) {
  const seen = new Set();
  return data.filter((item) => {
    const isDuplicate = seen.has(item.id);
    seen.add(item.id);
    return !isDuplicate;
  });
}

watch(activeTabIndex, (newIndex) => {
  localStorage.setItem('activeTabIndex', newIndex.toString());
})

onMounted(async () => {
  loading.value = true;
  await fetchBatches();
  loading.value = false;
  activeTabIndex.value = Number(localStorage.getItem('activeTabIndex')) || 0;
});
</script>

<template>
  <div class="p-10 bg-white rounded-xl">
    <TabView
      :pt="tabviewJustified"
      :pt-options="{
      mergeProps: false,
      mergeSections: false
    }" v-model:active-index="activeTabIndex">
      <TabPanel>
        <template #header>
          <IconTabHeader icon="ph:yarn-fill" label="Greig" />
        </template>
        <GreigTabPanelContent :batchList="greigBatches" />
      </TabPanel>

      <TabPanel>
        <template #header>
          <IconTabHeader icon="mdi:fabric" label="Offsite" />
        </template>
        <OffsiteTabPanelContent :batchList="offsiteBatches" />
      </TabPanel>

      <TabPanel>
        <template #header>
          <IconTabHeader icon="material-symbols:fact-check" label="Finished" />
        </template>
        <FinishedTabPanelContent :batchList="finishedBatches" />
      </TabPanel>

      <TabPanel>
        <template #header>
          <IconTabHeader icon="lets-icons:check-fill" label="Available" />
        </template>
        <AvailableTabPanelContent :batchList="availableBatches" />
      </TabPanel>

      <TabPanel>
        <template #header>
          <IconTabHeader icon="mdi:truck-fast" label="Dispatched" />
        </template>
        <DispatchedTabPanelContent :batch-list="dispatchedBatches"/>
      </TabPanel>
    </TabView>
  </div>
</template>

<style scoped>

</style>